import authHeader from './auth-header'

const apiUrl = process.env.VUE_APP_API_URL

// The backend returns 401 with this message ONLY when the JWT is no longer
// valid (the session has truly expired). Other 401s, e.g. a staff member
// hitting an admin-only endpoint, carry a different message and must NOT
// force a re-login. We therefore match on the message, not the status code.
const SESSION_EXPIRED_MARKER = 'session has expired'

// Synchronous module-level flag so the router guard can read expiry state
// immediately after an awaited request rejects (routing it through the store
// would resolve a microtask later and race the guard).
let sessionExpired = false
let redirecting = false

export function isSessionExpired() {
  return sessionExpired
}

export function resetSessionExpiry() {
  sessionExpired = false
  redirecting = false
}

function errorMessage(data) {
  if (!data) return ''
  if (typeof data === 'string') return data
  return (
    data.message
    || data.error
    || (data.errorData && data.errorData.message)
    || (data.data && data.data.message)
    || ''
  )
}

function isSessionExpiredResponse(status, data) {
  return status === 401 && errorMessage(data).toLowerCase().includes(SESSION_EXPIRED_MARKER)
}

function redirectToReLogin() {
  if (redirecting) return
  redirecting = true
  // Dynamic import avoids a module cycle (router -> store -> services -> requests).
  import('@/router').then(({ default: router }) => {
    const { name, query } = router.currentRoute
    if (name !== 'session-unavailable' || query.reason !== 'expired') {
      router.push({ name: 'session-unavailable', query: { reason: 'expired' } }).catch(() => {})
    }
  })
}

function handleResponse(response) {
  return response.text().then(text => {
    const data = text && JSON.parse(text)
    if (!response.ok) {
      if (isSessionExpiredResponse(response.status, data)) {
        sessionExpired = true
        redirectToReLogin()
      }
      return Promise.reject(data)
    }

    return data
  })
}

function dataRequest(url, payload, method) {
  const requestOptions = {
    method,
    headers: { ...authHeader(), ...{ 'Content-Type': 'application/json' } },
    body: JSON.stringify(payload),
  }

  return fetch(`${apiUrl}${url}`, requestOptions)
    .then(handleResponse)
    .then(data => data)
}

function get(url) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
  }

  return fetch(`${apiUrl}${url}`, requestOptions)
    .then(handleResponse)
    .then(data => data)
}

function getBlob(url) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
  }

  return fetch(`${apiUrl}${url}`, requestOptions)
    .then(response => response.blob())
    .then(data => data)
}

function post(url, payload) {
  return dataRequest(url, payload, 'POST')
}

function upload(url, data) {
  const requestOptions = {
    method: 'POST',
    headers: authHeader(),
    body: data,
  }

  return fetch(`${apiUrl}${url}`, requestOptions)
    .then(handleResponse)
    // eslint-disable-next-line no-shadow
    .then(data => data)
}

function put(url, payload) {
  return dataRequest(url, payload, 'PUT')
}

function deleteReq(url, payload) {
  return dataRequest(url, payload, 'DELETE')
}

export const requests = {
  get,
  getBlob,
  post,
  put,
  deleteReq,
  upload,
  handleResponse,
}

export default requests
